/**
 * Materials by status — Board 2 (`docs/visit-page.md` §8.2, §8.3), and the
 * compact row Board 18 C draws, which is what an iPad's narrower panel takes.
 *
 * A record table drawn by this page's own components (do-not-substitute 0):
 * the groups in §8.2's order, a row per line, the line opened in place to its
 * story and what it costs the visit. Every figure is the server's.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type {
  MaterialCostBox, MaterialGroup, MaterialLine, MaterialLineDetail, MaterialsByStatus, MaterialStoryEvent,
  VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { VisitGlyph } from './glyphs.js'
import { COMMON, MATERIALS, ORDER, RENTALS, SWAP } from './copy.js'
import { dayText, money, timeIn } from './format.js'
import { visitPath } from './api.js'
import { request } from '../lib/api.js'
import {
  DayChips, DeltaText, GROUP_DOT, MaterialChip, MoreButton, NotedTag, PoLink, RowButton, SourceCell, SwappedTag, poHref, qtyText, unitWord, count,
} from './materials-parts.js'
import { SLOT, fill } from './fields.js'
import { InternalChip } from './internal-chip.js'

/** The ways a row can ask for something — the tab owns the sheets and the menu. */
export interface LineActions {
  order: (line: MaterialLine) => void
  swap: (line: MaterialLine) => void
  menu: (line: MaterialLine, anchor: HTMLElement) => void
  /** The line whose ⋯ is open, so its button reads as pressed. */
  menuFor: string | null
}

/** Board 2's columns: *Material · Qty · Needed · Comes from · Where it stands · Est. cost · (actions)*. */
const COLUMNS = '2.1fr 78px 92px 1.3fr 1.7fr 124px 140px'
/** The same without *Est. cost* — a role without *Costs & margins* (§2.3: the cost columns are not drawn). */
const COLUMNS_HOURS_ONLY = '2.1fr 78px 92px 1.3fr 1.7fr 140px'
/** Board 18 C's line: the name and its one line, where it stands, the ⋯. */
const COLUMNS_COMPACT = '2.1fr 1.7fr 52px'

export function StatusTable({
  data, read, compact, openLine, onOpenLine, actions, canEdit,
}: {
  data: MaterialsByStatus
  read: VisitRead
  /** The panel is narrower than Board 2's table needs (an iPad) — Board 18 C's row. */
  compact: boolean
  openLine: string | null
  onOpenLine: (lineId: string | null) => void
  actions: LineActions
  canEdit: boolean
}) {
  const internal = data.internal !== undefined
  const columns = compact ? COLUMNS_COMPACT : internal ? COLUMNS : COLUMNS_HOURS_ONLY
  /* Board 18 C draws the line noted last — the one the office just noted. */
  const notedLast = compact
    ? data.groups.flatMap((group) => group.lines)
      .filter((line) => onNotedOrder(line, read) && line.detail.state === 'ordered' && line.detail.noted)
      .sort((a, b) => (a.detail.state === 'ordered' && b.detail.state === 'ordered'
        ? (b.detail.noted?.at ?? '').localeCompare(a.detail.noted?.at ?? '') : 0))[0]?.id ?? null
    : null
  return (
    <div className="overflow-hidden rounded-record border border-line bg-surface" data-materials-table="">
      <div
        className="grid h-[34px] items-center gap-3 border-b border-line bg-surface-alt px-[18px] text-label uppercase
                   tracking-[.06em] text-text-faint"
        style={{ gridTemplateColumns: columns }}
      >
        <span className="whitespace-nowrap">{MATERIALS.head.material}</span>
        {!compact && <span className="whitespace-nowrap">{MATERIALS.head.qty}</span>}
        {!compact && <span className="whitespace-nowrap">{MATERIALS.head.needed}</span>}
        {!compact && <span className="whitespace-nowrap">{MATERIALS.head.comesFrom}</span>}
        <span className="whitespace-nowrap">{MATERIALS.head.whereItStands}</span>
        {!compact && internal && <span className="whitespace-nowrap text-right">{MATERIALS.head.estCost}</span>}
        <span />
      </div>

      {data.groups.map((group) => (
        <GroupRows
          key={group.key}
          group={group}
          read={read}
          columns={columns}
          compact={compact}
          internal={internal}
          openLine={openLine}
          onOpenLine={onOpenLine}
          actions={actions}
          canEdit={canEdit}
          notedLast={notedLast}
        />
      ))}

      <div
        className="grid h-[46px] items-stretch gap-3 bg-surface-alt px-[18px] text-[13px]"
        style={{ gridTemplateColumns: columns }}
      >
        <div className="flex items-center"><b>{MATERIALS.footer(count(data.footer.count))}</b></div>
        <div
          className="flex items-center text-[12px] text-text-muted"
          style={{ gridColumn: compact ? '2 / 3' : '2 / 6' }}
        >
          {MATERIALS.footerSub}
        </div>
        {!compact && data.internal && (
          <div className="flex items-center justify-end border-l border-line-soft bg-surface-internal px-2.5">
            <b>{money(data.internal.footerTotal)}</b>
          </div>
        )}
      </div>
    </div>
  )
}

function GroupRows({
  group, read, columns, compact, internal, openLine, onOpenLine, actions, canEdit, notedLast,
}: {
  group: MaterialGroup
  read: VisitRead
  columns: string
  compact: boolean
  internal: boolean
  openLine: string | null
  onOpenLine: (lineId: string | null) => void
  actions: LineActions
  canEdit: boolean
  notedLast: string | null
}) {
  return (
    <>
      <div className="flex h-9 items-center gap-2 border-b border-line-faint bg-surface px-[18px]">
        <i aria-hidden="true" className={cn('inline-block h-2 w-2 rounded-full', GROUP_DOT[group.key])} />
        <b className="text-[12.5px]">{MATERIALS.groups[group.key]}</b>
        <span className="text-[12.5px] text-text-muted">{COMMON.count(count(group.count))}</span>
        <span className="flex-1" />
        {group.neededTomorrow && (
          <span className="text-[11.5px] font-semibold text-warn">{MATERIALS.neededWhen(COMMON.tomorrow)}</span>
        )}
      </div>
      {group.lines.map((line) => (
        <LineRow
          key={line.id}
          line={line}
          read={read}
          columns={columns}
          compact={compact}
          internal={internal}
          open={openLine === line.id}
          onToggle={() => onOpenLine(openLine === line.id ? null : line.id)}
          actions={actions}
          canEdit={canEdit}
          asDrawn={line.id === notedLast ? 'vp-18 C line' : undefined}
        />
      ))}
    </>
  )
}

/** Whether a line's ⋯ is Board 18 C's — on a noted PO while purchasing is off (§27.3). */
export function onNotedOrder(line: MaterialLine, read: VisitRead): boolean {
  return line.po?.entry === 'noted' && !read.capabilities['purchasing.orders']
}

function LineRow({
  line, read, columns, compact, internal, open, onToggle, actions, canEdit, asDrawn,
}: {
  line: MaterialLine
  read: VisitRead
  columns: string
  compact: boolean
  internal: boolean
  open: boolean
  onToggle: () => void
  actions: LineActions
  canEdit: boolean
  asDrawn?: string | undefined
}) {
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-line={line.id}
        data-as-drawn={asDrawn}
        onClick={onToggle}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onToggle() } }}
        className={cn(
          'grid cursor-pointer gap-3 border-b border-line-faint text-[13px] outline-none',
          /* Board 18 C's row: 12 inside its card's 1 px border, so 13 above the content here (the border is the row's above). */
          compact ? 'items-center px-4 pb-3 pt-[13px]' : 'min-h-[58px] items-stretch px-[18px] py-2',
          open ? 'bg-green-band-bg' : line.state === 'to_order' && compact ? 'bg-amber-row' : 'hover:bg-hover/60',
          'focus-visible:shadow-[inset_0_0_0_2px_rgb(var(--color-accent-ring))]',
        )}
        style={{ gridTemplateColumns: columns }}
      >
        <Cell>
          <div className="flex min-w-0 flex-col">
            <div className="flex min-w-0 items-center gap-1.5">
              <b className="truncate text-[13px] font-semibold">{line.names.name}</b>
              {line.swapped && <SwappedTag />}
            </div>
            <span className="truncate text-[11.5px] text-text-muted">{compact ? compactSub(line) : nameSub(line)}</span>
          </div>
        </Cell>

        {!compact && (
          <Cell>
            <span className="whitespace-nowrap">
              <b className="text-[13px]">{count(line.qty)}</b>
              {line.unit && <span className="text-[12px] text-text-muted">{` ${unitWord(line.qty, line.unit)}`}</span>}
            </span>
          </Cell>
        )}
        {!compact && <Cell><DayChips days={line.needed} /></Cell>}
        {!compact && <Cell><SourceCell line={line} /></Cell>}

        <Cell>
          <div className="flex min-w-0 flex-col items-start">
            <span className="flex items-center gap-1.5">
              <MaterialChip state={line.state} />
              {line.po?.entry === 'noted' && <NotedTag />}
            </span>
            <span className={cn('mt-1 text-[11.5px] text-text-muted', compact ? 'leading-[normal]' : 'leading-[1.35]')}>
              <StateDetail line={line} read={read} link />
            </span>
            {compact && canEdit && (line.actions.order || line.actions.swapButton) && (
              <span className="mt-2 flex items-center gap-1.5">
                {line.actions.order && <RowButton primary onClick={() => actions.order(line)}>{MATERIALS.order}</RowButton>}
                {line.actions.swapButton && (
                  <RowButton {...(line.actions.order ? {} : { glyph: 'swap' as const })} onClick={() => actions.swap(line)}>
                    {MATERIALS.swap}
                  </RowButton>
                )}
              </span>
            )}
          </div>
        </Cell>

        {!compact && internal && line.internal && (
          <div className="-my-2 flex flex-col items-end justify-center border-l border-line-soft bg-surface-internal px-2.5 py-2 text-right">
            <span className="whitespace-nowrap text-[13px]">
              {line.internal.now !== null ? (
                <>
                  <s className="text-text-faint">{money(line.internal.estimate)}</s>
                  {' '}
                  <b>{money(line.internal.now)}</b>
                </>
              ) : money(line.internal.estimate)}
            </span>
            <span className="whitespace-nowrap text-[11px] text-text-faint">
              {line.internal.unitNow !== null
                ? MATERIALS.costChange(money(line.internal.unitEstimate), money(line.internal.unitNow), eachWord(line))
                : MATERIALS.perUnit(money(line.internal.unitEstimate), eachWord(line))}
            </span>
          </div>
        )}

        <div className="flex min-w-0 items-center justify-end text-right">
          {canEdit && (compact
            ? !line.actions.order && (
              <MoreButton open={actions.menuFor === line.id} label={line.names.name} onClick={(anchor) => actions.menu(line, anchor)} />
            )
            : <RowActions line={line} actions={actions} />)}
        </div>
      </div>
      {open && <LineOpened line={line} read={read} compact={compact} />}
    </>
  )
}

function Cell({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 items-center self-stretch">{children}</div>
}

/** *each* when a line is sold by the each, else its unit — *$12.75 → $14.20 each*, *$4.85 / sq ft*. */
function eachWord(line: MaterialLine): string {
  return line.unit ? line.unit.one : MATERIALS.each
}

/** The line under a line's name — its detail, or a part-used line's *modified stone · 12 t used*. */
function nameSub(line: MaterialLine): string {
  if (line.usedSoFar) return MATERIALS.partUsed(line.names.detail ?? '', qtyText(line.usedSoFar.qty, line.usedSoFar.unit))
  return line.names.detail ?? ''
}

/** Board 18 C's one line — *gas-ready · 1 · Day 3 · crew picks up*. */
function compactSub(line: MaterialLine): string {
  const source = line.source === 'yard' || line.source === 'field'
    ? MATERIALS.source[line.source]
    : MATERIALS.sourceInLine[line.source]
  return MATERIALS.lineSub(
    line.names.detail ?? '', qtyText(line.qty, line.unit), String(line.needed[0]?.index ?? ''), source,
  )
}

/** A row's actions (§8.3, §9.1): **Order** and **Swap** on *To order*; **Swap** and ⋯ on *At the yard*; ⋯ otherwise. */
function RowActions({ line, actions }: { line: MaterialLine; actions: LineActions }) {
  const more = (
    <MoreButton open={actions.menuFor === line.id} label={line.names.name} onClick={(anchor) => actions.menu(line, anchor)} />
  )
  if (line.actions.order) {
    return (
      <span className="flex items-center justify-end gap-1.5">
        <RowButton primary onClick={() => actions.order(line)}>{MATERIALS.order}</RowButton>
        {line.actions.swapButton && <RowButton onClick={() => actions.swap(line)}>{MATERIALS.swap}</RowButton>}
      </span>
    )
  }
  return (
    <span className="flex items-center justify-end gap-2">
      {line.actions.swapButton && <RowButton glyph="swap" onClick={() => actions.swap(line)}>{MATERIALS.swap}</RowButton>}
      {more}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Where it stands, in words (§8.2's details)
// ---------------------------------------------------------------------------

/** The detail beside the chip — the PO green and clickable when `link`. */
export function StateDetail({ line, read, link }: { line: MaterialLine; read: VisitRead; link: boolean }) {
  const tz = read.timeZone
  const detail = line.detail
  const po = (ref: { id: string; number: string }) => (link ? <PoLink po={ref} /> : ref.number)
  switch (detail.state) {
    case 'to_order':
      return <>{detail.neededBy ? MATERIALS.detail.notOrdered(timeIn(tz, detail.neededBy, 'day'), timeIn(tz, detail.neededBy, 'clock')) : null}</>
    case 'ordered':
      if (detail.noted) {
        return <>{fill(MATERIALS.detail.noted(detail.noted.by.name, timeIn(tz, detail.noted.at, 'dayAmpm'), SLOT), po(detail.po))}</>
      }
      if (detail.added) {
        return <>{fill(ORDER.addedLine(SLOT, timeIn(tz, detail.added.at, 'ampm'), detail.added.by.name), po(detail.po))}</>
      }
      if (detail.arrives) {
        const when = detail.arrives.relative ? COMMON[detail.arrives.relative] : timeIn(tz, detail.arrives.at, 'day')
        return <>{fill(MATERIALS.detail.arrives(when, timeIn(tz, detail.arrives.at, 'hourAmpm'), SLOT), po(detail.po))}</>
      }
      return <>{po(detail.po)}</>
    case 'ready_pickup':
      if (detail.picker && detail.at) {
        return <>{fill(MATERIALS.detail.picksUp(detail.picker.firstName, timeIn(tz, detail.at, 'dayClock'), SLOT), po(detail.po))}</>
      }
      return <>{po(detail.po)}</>
    case 'on_site':
      return detail.po
        ? <>{fill(MATERIALS.detail.delivered(dayText(detail.on, 'date'), SLOT), po(detail.po))}</>
        : <>{MATERIALS.detail.deliveredOn(dayText(detail.on, 'date'))}</>
    case 'loaded':
      return detail.usedBefore
        ? <>{MATERIALS.detail.usedLoaded(qtyText(detail.usedBefore.qty, detail.usedBefore.unit), dayText(detail.usedBefore.on, 'day'), qtyText(detail.qty, detail.unit))}</>
        : <>{MATERIALS.detail.loadedThisMorning}</>
    case 'at_yard':
      return <>{detail.held ? MATERIALS.detail.held : null}</>
    case 'bought':
      return <>{MATERIALS.detail.bought(detail.vendor, dayText(detail.on, 'day'))}</>
    case 'used':
      return <>{MATERIALS.detail.used(dayText(detail.on, 'day'))}</>
  }
}

// ---------------------------------------------------------------------------
// A line opened in place (Board 2: the wall caps)
// ---------------------------------------------------------------------------

/** A line opened in place — on a phone with its actions under it (`children`). */
export function LineOpened({ line, read, compact, children }: {
  line: MaterialLine
  read: VisitRead
  compact: boolean
  children?: ReactNode
}) {
  const [detail, setDetail] = useState<MaterialLineDetail | null>(null)
  useEffect(() => {
    let live = true
    request<MaterialLineDetail>(visitPath(read.id, `/materials/${encodeURIComponent(line.id)}`))
      .then((body) => { if (live) setDetail(body) })
      .catch(() => { /* the row stays open with nothing under it; the next open asks again */ })
    return () => { live = false }
  }, [read.id, line.id, line.version])

  return (
    <div
      className={cn(
        'grid gap-7 border-b border-line bg-green-band-bg',
        compact ? 'grid-cols-1 px-4 pb-1.5 pt-3.5' : 'grid-cols-[1.25fr_1fr] pb-1.5 pl-[46px] pr-[18px] pt-3.5',
      )}
    >
      <div>
        <div className="mb-2.5 text-label uppercase tracking-[.06em] text-text-faint">{MATERIALS.story.title}</div>
        {detail?.story.map((event, index) => (
          <StoryStep
            key={index}
            event={event}
            line={line}
            read={read}
            last={index === detail.story.length - 1}
          />
        ))}
      </div>
      <div className="flex flex-col gap-2.5 pb-3">
        {detail?.internal && (
          <>
            <div className="text-label uppercase tracking-[.06em] text-text-faint">{MATERIALS.costs.title}</div>
            <CostBox cost={detail.internal.cost} />
          </>
        )}
        {detail && (detail.po || detail.swapNoteId) && (
          <div className="flex items-center gap-2">
            {detail.po && (
              <Link
                to={poHref(detail.po)}
                className="inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-button border border-line bg-surface
                           px-3.5 text-[12px] font-semibold text-text hover:bg-hover"
              >
                <VisitGlyph name="cart" size={14} stroke={2} />
                {COMMON.open(detail.po.number)}
              </Link>
            )}
            {detail.swapNoteId && (
              <Link
                to={`/jobs/${encodeURIComponent(read.header.meta.job.id)}?tab=activity&note=${encodeURIComponent(detail.swapNoteId)}`}
                className="inline-flex h-[30px] items-center whitespace-nowrap rounded-button border border-line bg-surface px-3.5
                           text-[12px] font-semibold text-text hover:bg-hover"
              >
                {MATERIALS.costs.openSwapNote}
              </Link>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

/** **What it costs the visit** — internal (Board 2). */
function CostBox({ cost }: { cost: MaterialCostBox }) {
  const billing = cost.billing === 'fixed' ? COMMON.fixedPrice
    : cost.billing === 'time_materials' ? COMMON.timeAndMaterials : COMMON.perVisit
  const qty = count(cost.qty)
  return (
    <div className="rounded-[10px] border border-line bg-surface-internal px-3 py-2.5 text-[12.5px] leading-[1.55] text-text-body">
      <div className="flex items-center gap-2">
        <InternalChip />
        <b className="text-text">{billing}</b>
      </div>
      <div className="mt-1.5">
        {fill(MATERIALS.costs.planned(qty, money(cost.unitPlanned), SLOT), <b className="text-text">{money(cost.planned)}</b>)}
        <br />
        {fill(MATERIALS.costs.now(qty, money(cost.unitNow), SLOT), <b className="text-text">{money(cost.now)}</b>)}
        {cost.billing !== 'time_materials' && cost.visitPays.tone !== 'zero' && (
          <>
            {' '}
            {fill(MATERIALS.costs.visitPays(SLOT), <DeltaText delta={cost.visitPays} className="font-bold" />)}
          </>
        )}
      </div>
      {cost.billing !== 'time_materials' && cost.customerFirstName && cost.salesperson && !cost.changeOrderAsked && (
        <div className="mt-1.5 text-[12px] text-text-muted">
          {MATERIALS.costs.priceStays(cost.customerFirstName, cost.salesperson.name)}
        </div>
      )}
    </div>
  )
}

/** One step of a line's story — a dot on the rail, its title and time, the line under it. */
function StoryStep({ event, line, read, last }: {
  event: MaterialStoryEvent
  line: MaterialLine
  read: VisitRead
  last: boolean
}) {
  const tz = read.timeZone
  const words = storyWords(event, line, tz)
  const tone = event.kind === 'swapped' ? 'amber' : event.kind === 'arriving' ? 'hollow' : 'green'
  return (
    <div className="relative flex gap-3 pb-3">
      <div className="flex flex-col items-center pt-[3px]">
        <i
          aria-hidden="true"
          className={cn(
            'block h-3 w-3 rounded-full border-2',
            tone === 'hollow' ? 'border-line-strong bg-surface' : 'border-surface',
            tone === 'green' && 'bg-accent shadow-[0_0_0_1px_rgb(var(--color-accent))]',
            tone === 'amber' && 'bg-warn shadow-[0_0_0_1px_rgb(var(--color-warn))]',
          )}
        />
        {!last && <i aria-hidden="true" className="mt-[3px] block w-0.5 flex-1 bg-line" />}
      </div>
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-2">
          <b className="text-[12.5px]">{words.title}</b>
          <span className="whitespace-nowrap text-[11px] text-text-faint">{words.when}</span>
        </div>
        {words.sub && <span className="mt-px text-[11.5px] text-text-muted">{words.sub}</span>}
      </div>
    </div>
  )
}

function storyWords(event: MaterialStoryEvent, line: MaterialLine, tz: string): { title: string; when: string; sub: string } {
  const S = MATERIALS.story
  const P = MATERIALS.storyParts
  const relativeOr = (at: string, relative: 'today' | 'tomorrow' | null) => (relative ? COMMON[relative] : timeIn(tz, at, 'dayAmpm'))
  switch (event.kind) {
    case 'planned': {
      const parts = [event.item.name, qtyText(event.qty, event.unit)]
      if (event.internal) parts.push(money(event.internal.unitCost))
      return {
        title: event.quoteReference ? S.planned(event.quoteReference) : COMMON.plannedLabel,
        when: timeIn(tz, event.at, 'monthDay'),
        sub: parts.reduce((all, part) => COMMON.dot(all, part)),
      }
    }
    case 'backordered':
      return {
        title: event.until ? S.backordered(event.vendor, dayText(event.until, 'monthDay')) : event.vendor,
        when: timeIn(tz, event.at, 'dayAmpm'),
        sub: event.by ? S.loggedBy(event.by.name) : '',
      }
    case 'swapped': {
      const to = event.to.model ? `${event.to.model}, ${event.to.detail ?? ''}`.replace(/, $/, '') : event.to.name
      const reason = (SWAP.reasonInSentence as Record<string, string>)[event.reason]
      const parts = [reason ? P.swappedWho(event.by.name, reason) : event.by.name]
      if (event.internal && event.billing !== 'time_materials') parts.push(P.swappedPays(signedText(event.internal.visitPays)))
      if (event.note) parts.push(event.note)
      return {
        title: event.internal ? S.swapped(to, money(event.internal.unitCost)) : P.swappedTo(to),
        when: timeIn(tz, event.at, 'dayAmpm'),
        sub: parts.reduce((all, part) => COMMON.dot(all, part)),
      }
    }
    case 'ordered':
      return {
        title: S.ordered(event.po.number, ORDER.toInLine[event.to]),
        when: timeIn(tz, event.at, 'dayAmpm'),
        sub: event.by && event.confirms
          ? S.confirms(event.by.name, event.vendor, timeIn(tz, event.confirms.at, 'day'), timeIn(tz, event.confirms.at, 'hourAmpm'))
          : event.by?.name ?? event.vendor,
      }
    case 'noted':
      return {
        title: MATERIALS.detail.noted(event.by.name, timeIn(tz, event.at, 'dayAmpm'), event.po.number),
        when: '',
        sub: '',
      }
    case 'arriving': {
      const loadDay = line.needed.find((dayRef) => dayRef.index === event.loadsForDay)
      return {
        title: event.loadsForDay !== null ? S.arrivesThenLoads(String(event.loadsForDay)) : MATERIALS.state.ordered,
        when: relativeOr(event.at, event.relative),
        sub: loadDay ? S.receiverTicks(dayText(loadDay.date, 'weekday')) : '',
      }
    }
    default: {
      const title = event.kind === 'delivered' ? MATERIALS.state.on_site
        : event.kind === 'loaded' ? MATERIALS.state.loaded
          : event.kind === 'used' ? MATERIALS.state.used
            : event.kind === 'bought' ? MATERIALS.state.bought
              : RENTALS.state.returned
      const parts = [event.by?.name, event.qty !== null ? qtyText(event.qty, event.unit) : null].filter((part): part is string => Boolean(part))
      return {
        title,
        when: timeIn(tz, event.at, 'dayAmpm'),
        sub: parts.length ? parts.reduce((all, part) => COMMON.dot(all, part)) : '',
      }
    }
  }
}

/** *+$34.80* as text, for a sentence that carries it. */
function signedText(delta: { signed: string }): string {
  const sign = delta.signed.startsWith('-') ? '−' : delta.signed.startsWith('+') ? '+' : ''
  return `${sign}${money(delta.signed.replace(/^[+-]/, ''))}`
}
